import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { useQuery } from '@tanstack/react-query'
import { Badge, Box, Card, Flex, Grid, Heading, Progress, Text } from '@radix-ui/themes'
import { useMemo } from 'react'
import cn from 'classnames'
import { useTRPC } from '~/utils/trpc'
import { stageColor, stageLabel, type CompatibilityStatus, type Stage } from '~/features/installations/constants'
import {
  ACTIVITY_METRICS,
  ACTIVITY_TREND_WEEKS,
  activityMetricLabel,
  computeActivityTrend,
  computeOverview,
  FIRST_TRANSMISSION_DELAY_HOURS,
  INDEX_ALIGNMENT_DELAY_DAYS,
  type ActivityTrendPoint,
  type OverviewKPIs,
  type StagePipelineEntry
} from '~/features/installations/overview'
import { TimeSeriesChart, type ChartSeries } from '~/components/Charts/TimeSeriesChart'
import { colorAtIndex } from '~/components/Charts/tokens'
import { PATCHY_DATA_LABEL } from '~/features/stations/data-coverage/format'
import { COVERAGE_STALE_MS } from '~/features/stations/data-coverage/query'

const formatCount = (value: number): string => value.toLocaleString('en-GB')

const activitySeries: ChartSeries[] = ACTIVITY_METRICS.map((metric, i) => ({
  key: metric,
  label: activityMetricLabel[metric],
  color: colorAtIndex(i)
}))

// The current (in-progress) week is marked with a trailing "*" on its x-axis label so a
// partial bar isn't mistaken for a completed week. isPartial is dropped here since the chart
// data only carries string | number values.
type ActivityChartPoint = Omit<ActivityTrendPoint, 'isPartial'>

const withPartialLabels = (trend: ActivityTrendPoint[]): ActivityChartPoint[] =>
  trend.map(({ isPartial, ...point }) => (isPartial ? { ...point, date: `${point.date} *` } : point))

const ActivityTrendChart = ({ trend }: { trend: ActivityTrendPoint[] }) => (
  <Flex direction="column" gap="3">
    <Flex align="baseline" gap="2" wrap="wrap">
      <Heading size="3">Activity — last {ACTIVITY_TREND_WEEKS} weeks</Heading>
      <Text size="1" color="gray">
        (calendar weeks, Mon–Sun · * current week in progress)
      </Text>
    </Flex>
    <Card size="3">
      <Flex direction="column" gap="3">
        <Flex gap="4" wrap="wrap">
          {activitySeries.map((series) => (
            <Flex key={series.key} align="center" gap="2">
              <span className="h-2 w-2 rounded-full shrink-0" style={{ backgroundColor: series.color }} />
              <Text size="1" color="gray">
                {series.label}
              </Text>
            </Flex>
          ))}
        </Flex>
        <Box height="280px">
          <TimeSeriesChart
            data={withPartialLabels(trend)}
            xKey="date"
            series={activitySeries}
            chartType="line"
            valueFormatter={formatCount}
            yAxisWidth={32}
          />
        </Box>
      </Flex>
    </Card>
  </Flex>
)

interface KPIItem {
  label: string
  value: string
  hint?: string
}

const KPICards = ({ kpis }: { kpis: OverviewKPIs }) => {
  const items: KPIItem[] = [
    {
      label: 'Total installations',
      value: kpis.totalInstallations.toString(),
      // Named where the number is read, since every figure on this page leaves them out.
      hint: kpis.retiredCount > 0 ? `${kpis.retiredCount} retired (excluded)` : undefined
    },
    { label: 'Active orgs', value: kpis.activeOrgs.toString() },
    {
      label: 'Installed',
      value: `${kpis.installedCount} (${kpis.installedPercent}%)`
    },
    {
      label: 'Index aligned',
      value: `${kpis.indexAlignedCount} (${kpis.indexAlignedPercent}%)`
    }
  ]
  return (
    <Grid columns={{ initial: '2', md: '4' }} gap="3">
      {items.map((item) => (
        <Card key={item.label} size="2">
          <Flex direction="column" gap="1">
            <Text size="1" color="gray">
              {item.label}
            </Text>
            <Text size="6" weight="bold">
              {item.value}
            </Text>
            {item.hint ? (
              <Text size="1" color="gray">
                {item.hint}
              </Text>
            ) : null}
          </Flex>
        </Card>
      ))}
    </Grid>
  )
}

const PipelineFunnel = ({
  pipeline,
  total,
  onStageClick
}: {
  pipeline: StagePipelineEntry[]
  total: number
  onStageClick: (stage: Stage) => void
}) => {
  const max = Math.max(1, ...pipeline.map((p) => p.count))
  return (
    <Flex direction="column" gap="3">
      <Heading size="3">Pipeline</Heading>
      <Card size="3">
        <Flex direction="column" gap="2">
          {pipeline.map((entry) => {
            const widthPct = Math.round((entry.count / max) * 100)
            const sharePct = total === 0 ? 0 : Math.round((entry.count / total) * 100)
            return (
              <button
                key={entry.stage}
                type="button"
                onClick={() => onStageClick(entry.stage)}
                className="text-left rounded-md p-2 hover:bg-[var(--gray-a2)] cursor-pointer"
              >
                <Flex align="center" gap="3">
                  <Box width="160px" className="shrink-0">
                    <Badge color={stageColor[entry.stage]} variant="soft" size="1">
                      {stageLabel[entry.stage]}
                    </Badge>
                  </Box>
                  <Box className="flex-1">
                    <Progress value={widthPct} color={stageColor[entry.stage]} size="2" />
                  </Box>
                  <Box width="100px" className="shrink-0 text-right">
                    <Text size="2" weight="medium">
                      {entry.count}
                    </Text>
                    <Text size="1" color="gray">
                      {' '}
                      ({sharePct}%)
                    </Text>
                  </Box>
                </Flex>
              </button>
            )
          })}
        </Flex>
      </Card>
    </Flex>
  )
}

interface AttentionItem {
  key: string
  label: string
  value: number
  onClick: () => void
}

const AttentionCards = ({ items }: { items: AttentionItem[] }) => (
  <Flex direction="column" gap="3">
    <Heading size="3">Attention needed</Heading>
    <Grid columns={{ initial: '1', md: '2' }} gap="3">
      {items.map((item) => (
        <Card
          key={item.key}
          size="2"
          asChild
          className={cn('transition-colors', item.value > 0 && 'cursor-pointer hover:bg-[var(--gray-a2)]')}
        >
          <button type="button" disabled={item.value === 0} onClick={item.onClick} className="text-left w-full">
            <Flex direction="column" gap="1">
              <Text size="1" color="gray">
                {item.label}
              </Text>
              <Text size="6" weight="bold" color={item.value > 0 ? 'red' : undefined}>
                {item.value}
              </Text>
            </Flex>
          </button>
        </Card>
      ))}
    </Grid>
  </Flex>
)

const HomePage = () => {
  const trpc = useTRPC()
  const navigate = useNavigate()

  // Retired rows are counted, not shown: computeOverview drops them from every figure, and the
  // activity trend keeps them so past weeks don't shrink when something is retired today.
  const installationsQuery = useQuery(trpc.ops.stationInstallations.list.queryOptions({ includeRetired: true }))
  const returnsInProgressQuery = useQuery(trpc.ops.materialReturns.list.queryOptions({ status: 'in_progress' }))
  const returnsInProgressCount = returnsInProgressQuery.data?.length ?? 0
  const belowCoverageQuery = useQuery(
    trpc.ops.stations.list.queryOptions({ coverage: 'below' }, { staleTime: COVERAGE_STALE_MS })
  )

  const overview = useMemo(
    () => (installationsQuery.data ? computeOverview(installationsQuery.data) : null),
    [installationsQuery.data]
  )

  const activityTrend = useMemo(
    () => (installationsQuery.data ? computeActivityTrend(installationsQuery.data) : null),
    [installationsQuery.data]
  )

  const goToInstallations = (search: {
    stage?: Stage
    compatibilityStatus?: CompatibilityStatus
    installationIds?: number[]
    verificationNeeded?: boolean
    compatibilityToCheck?: boolean
  }) => {
    navigate({ to: '/installations', search })
  }

  if (installationsQuery.isLoading) {
    return <Text color="gray">Loading…</Text>
  }
  if (installationsQuery.error) {
    return <Text color="red">{installationsQuery.error.message}</Text>
  }
  if (!overview || !activityTrend) {
    return <Text color="gray">No data.</Text>
  }

  return (
    <Flex direction="column" gap="5">
      <Heading size="6">Overview</Heading>

      <KPICards kpis={overview.kpis} />

      <ActivityTrendChart trend={activityTrend} />

      <Grid columns={{ initial: '1', lg: '2' }} gap="4">
        <PipelineFunnel
          pipeline={overview.pipeline}
          total={overview.kpis.totalInstallations}
          onStageClick={(stage) => goToInstallations({ stage })}
        />
        <AttentionCards
          items={[
            {
              key: 'indexAlignmentNeeded',
              label: `Index alignment needed (> ${INDEX_ALIGNMENT_DELAY_DAYS}d since install)`,
              value: overview.attention.indexAlignmentNeeded,
              onClick: () => goToInstallations({ installationIds: overview.indexAlignmentNeededIds })
            },
            {
              key: 'awaitingFirstTransmission',
              label: `No transmission yet (> ${FIRST_TRANSMISSION_DELAY_HOURS}h since install)`,
              value: overview.attention.awaitingFirstTransmission,
              onClick: () => goToInstallations({ installationIds: overview.awaitingFirstTransmissionIds })
            },
            {
              key: 'verificationNeeded',
              label: 'To verify (submitted from the app, no installation date)',
              value: overview.attention.verificationNeeded,
              onClick: () => goToInstallations({ verificationNeeded: true })
            },
            {
              key: 'compatibilityToCheck',
              label: 'Compatibility to check (image received, no verdict)',
              value: overview.compatibility.toCheck,
              onClick: () => goToInstallations({ compatibilityToCheck: true })
            },
            {
              key: 'notCompatible',
              label: 'Not compatible, to retire',
              value: overview.compatibility.notCompatibleUnretired,
              onClick: () => goToInstallations({ compatibilityStatus: 'not_compatible' })
            },
            {
              key: 'returnsInProgress',
              label: 'Material returns in progress',
              value: returnsInProgressCount,
              onClick: () => navigate({ to: '/returns', search: { status: 'in_progress' } })
            },
            {
              key: 'belowCoverage',
              label: PATCHY_DATA_LABEL,
              value: belowCoverageQuery.data?.total ?? 0,
              onClick: () => navigate({ to: '/stations', search: { coverage: 'below' } })
            }
          ]}
        />
      </Grid>
    </Flex>
  )
}

export const Route = createFileRoute('/')({
  component: HomePage
})
